import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { HumisLogo } from '@/components/brand/HumisLogo'
import { apiClient } from '@/lib/api/client'
import { formProblem } from '@/lib/api/fieldErrors'

/**
 * "Forgot password?" — an email field, and the same answer whatever was typed.
 *
 * <p>The answer never says whether the address has an account. It cannot: the server answers
 * the same way for both, so that this page is not a way to find out who works where. The only
 * thing that can come back different is a malformed address, which is about the typing, not
 * about anybody's account.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    setSending(true)
    try {
      await apiClient.post('/v1/auth/forgot-password', { email: email.trim() })
      setSent(true)
    } catch (caught) {
      const problem = formProblem(caught, t('auth.forgotSent'))
      setError(problem.fields.email ?? problem.summary)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-[var(--color-page)] p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <HumisLogo variant="full" alt="HUMIS" className="h-16 w-auto max-w-full" />
        </div>
        <div className="hrms-card p-6">
          <h1 className="text-lg font-semibold text-[var(--color-heading)]">{t('auth.forgotTitle')}</h1>
          {sent ? (
            <div className="mt-4 space-y-4">
              <p role="status" className="flex items-start gap-2 text-sm text-[var(--color-success)]">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                {t('auth.forgotSent')}
              </p>
              <Link to="/login" className="text-sm text-[var(--color-primary)] hover:underline">
                {t('auth.backToSignIn')}
              </Link>
            </div>
          ) : (
            <form
              className="mt-4 space-y-4"
              noValidate
              onSubmit={(event) => { event.preventDefault(); void submit() }}
            >
              <p className="text-sm text-[var(--color-muted)]">{t('auth.forgotSubtitle')}</p>
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email" required>{t('auth.email')}</Label>
                <Input
                  id="forgot-email" type="email" autoComplete="username" autoFocus required
                  value={email} invalid={Boolean(error)}
                  aria-describedby={error ? 'forgot-email-error' : undefined}
                  onChange={(event) => setEmail(event.target.value)}
                />
                {error && (
                  <p id="forgot-email-error" className="text-xs text-[var(--color-danger)]">{error}</p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={sending || !email.trim()}>
                {sending ? t('auth.sending') : t('auth.sendLink')}
              </Button>
              <Link to="/login" className="block text-center text-sm text-[var(--color-primary)] hover:underline">
                {t('auth.backToSignIn')}
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
